import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Tag,
  Tooltip,
  Input,
  Select,
  Modal,
  Form,
  Upload,
} from "antd";
import React, { useState, useEffect, useContext } from "react";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import ShowTotal from "../../components/ShowTotal";
import moment from "moment";
import apiPath from "../../constants/apiPath";
import { AuthContext } from "../../context/AuthContext";
import { USERTYPE } from "../../constants/conts";
import {
  ExclamationCircleOutlined,
  UndoOutlined,
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined,
  EditOutlined,
} from "@ant-design/icons";
import FilterBar, { FilterBarFilters } from "../../components/FilterBar";
import confirm from "antd/lib/modal/confirm";
import useDebounce from "../../hooks/useDebounce";

const { Option } = Select;
const Search = Input.Search;

function MusicManager() {
  const sectionName = "Music";
  const { userProfile, userPermission } = useContext(AuthContext);
  const { request } = useRequest();

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  
  // Modals state
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [form] = Form.useForm();
  const [file, setFile] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);

  // Edit Modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editForm] = Form.useForm();

  const closeAndResetModal = () => {
    setAddModalOpen(false);
    setFile(null);
    if (filePreviewUrl) {
      URL.revokeObjectURL(filePreviewUrl);
      setFilePreviewUrl(null);
    }
    form.resetFields();
  };

  // Search & Filters state
  const [searchText, setSearchText] = useState("");
  const debouncedSearchText = useDebounce(searchText, 300);
  const [filters, setFilters] = useState({ status: undefined });
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  const isSuperAdmin = userProfile?.role === USERTYPE.SUPER_ADMIN;
  
  // Custom permissions check for Music
  const isAddPermissionGranted = isSuperAdmin || userPermission?.MUSIC_MANAGER?.permissions?.create === true;
  const isDeletePermissionGranted = isSuperAdmin || userPermission?.MUSIC_MANAGER?.permissions?.delete === true;
  const isEditPermissionGranted = isSuperAdmin || userPermission?.MUSIC_MANAGER?.permissions?.update === true;

  const fetchData = (
    paginationParam = pagination,
    filtersParam = filters,
    searchVal = debouncedSearchText
  ) => {
    setLoading(true);
    const params = new URLSearchParams();

    if (searchVal) {
      params.append("search", searchVal);
    }
    if (filtersParam.status !== undefined && filtersParam.status !== null) {
      params.append("status", filtersParam.status);
    }

    params.append("page", paginationParam.current);
    params.append("limit", paginationParam.pageSize);

    request({
      url: `${apiPath.getMusic}?${params.toString()}`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        if (data.status) {
          setList(data.data.data || []);
          setPagination({
            current: data.data.page,
            total: data.data.total,
            pageSize: Number(data.data.limit),
          });
        }
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(error?.response?.data?.message || "Failed to load music playlist", Severty.ERROR);
      },
    });
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleReset = () => {
    const resetFilters = { status: undefined };
    setFilters(resetFilters);
    setSearchText("");

    const resetPagination = { ...pagination, current: 1 };
    setPagination(resetPagination);

    fetchData(resetPagination, resetFilters, "");
  };

  const toggleStatus = (record) => {
    confirm({
      maskClosable: true,
      icon: <ExclamationCircleOutlined />,
      title: "Change Status",
      content: `Are you sure you want to change the status of "${record.title}"?`,
      okText: "Yes",
      cancelText: "No",
      onOk() {
        request({
          url: `${apiPath.changeMusicStatus}/${record._id}`,
          method: "PUT",
          onSuccess: (data) => {
            if (data.status) {
              ShowToast(data.message || "Status updated successfully", Severty.SUCCESS);
              fetchData(pagination, filters);
            } else {
              ShowToast(data.message, Severty.ERROR);
            }
          },
          onError: (error) => {
            ShowToast(error?.response?.data?.message || "Failed to update status", Severty.ERROR);
          },
        });
      },
    });
  };

  const handleDelete = (record) => {
    confirm({
      maskClosable: true,
      icon: <ExclamationCircleOutlined />,
      title: "Delete Music",
      content: `Are you sure you want to delete "${record.title}"? This action cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk() {
        request({
          url: `${apiPath.deleteMusic}/${record._id}`,
          method: "DELETE",
          onSuccess: (data) => {
            if (data.status) {
              ShowToast(data.message || "Music deleted successfully", Severty.SUCCESS);
              fetchData(pagination, filters);
            } else {
              ShowToast(data.message, Severty.ERROR);
            }
          },
          onError: (error) => {
            ShowToast(error?.response?.data?.message || "Failed to delete music", Severty.ERROR);
          },
        });
      },
    });
  };

  const handleAddSubmit = (values) => {
    if (!file) {
      ShowToast("Please select a music file to upload", Severty.ERROR);
      return;
    }

    const formData = new FormData();
    formData.append("title", values.title);
    if (values.artist) {
      formData.append("artist", values.artist);
    }
    formData.append("file", file);

    setModalLoading(true);
    request({
      url: apiPath.addMusic,
      method: "POST",
      data: formData,
      header: {
        "Content-Type": "multipart/form-data",
      },
      onSuccess: (data) => {
        setModalLoading(false);
        if (data.status) {
          ShowToast("Music added successfully", Severty.SUCCESS);
          closeAndResetModal();
          fetchData({ ...pagination, current: 1 }, filters);
        } else {
          ShowToast(data.message || "Failed to add music", Severty.ERROR);
        }
      },
      onError: (error) => {
        setModalLoading(false);
        ShowToast(error?.response?.data?.message || "Failed to add music", Severty.ERROR);
      },
    });
  };

  const handleEditClick = (record) => {
    setSelectedRecord(record);
    editForm.setFieldsValue({
      title: record.title,
      artist: record.artist || "",
    });
    setEditModalOpen(true);
  };

  const handleEditSubmit = (values) => {
    setModalLoading(true);
    request({
      url: `${apiPath.updateMusic}/${selectedRecord._id}`,
      method: "PUT",
      data: {
        title: values.title,
        artist: values.artist || "",
      },
      onSuccess: (data) => {
        setModalLoading(false);
        if (data.status) {
          ShowToast("Music updated successfully", Severty.SUCCESS);
          setEditModalOpen(false);
          setSelectedRecord(null);
          editForm.resetFields();
          fetchData(pagination, filters);
        } else {
          ShowToast(data.message || "Failed to update music", Severty.ERROR);
        }
      },
      onError: (error) => {
        setModalLoading(false);
        ShowToast(error?.response?.data?.message || "Failed to update music", Severty.ERROR);
      },
    });
  };

  useEffect(() => {
    fetchData(pagination, filters);
  }, [
    pagination.current,
    pagination.pageSize,
    filters,
    refresh,
    debouncedSearchText,
  ]);

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      sorter: (a, b) => a.title?.localeCompare(b.title),
      render: (title) => <strong>{title}</strong>,
    },
    {
      title: "Artist",
      dataIndex: "artist",
      key: "artist",
      render: (artist) => artist || "N/A",
    },
    {
      title: "Preview",
      key: "preview",
      render: (_, record) => {
        return (
          <audio
            src={record.fileUrl}
            controls
            controlsList="nodownload"
            style={{ height: "32px", width: "240px", verticalAlign: "middle" }}
            onPlay={(e) => {
              const audios = document.getElementsByTagName("audio");
              for (let i = 0; i < audios.length; i++) {
                if (audios[i] !== e.target) {
                  audios[i].pause();
                }
              }
            }}
          />
        );
      },
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive, record) => (
        <Tag
          color={isActive ? "green" : "red"}
          style={{ cursor: "pointer" }}
          onClick={() => toggleStatus(record)}
        >
          {isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Uploaded On",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt) => moment(createdAt).format("DD-MM-YYYY HH:mm"),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <Tooltip title="Edit Music">
            <Button
              type="primary"
              disabled={!isEditPermissionGranted}
              icon={<EditOutlined />}
              onClick={() => handleEditClick(record)}
            />
          </Tooltip>
          <Tooltip title="Delete Music">
            <Button
              type="primary"
              danger
              disabled={!isDeletePermissionGranted}
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="tabled customerMain">
        <Row gutter={[24, 0]}>
          <Col xs={24}>
            <Card
              title="Music Manager"
              extra={
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <FilterBar
                    showFilters={showFilters}
                    onToggle={() => setShowFilters((v) => !v)}
                    searchContent={
                      <Search
                        allowClear
                        placeholder="Search by Title or Artist"
                        value={searchText}
                        onChange={(e) => {
                          setSearchText(e.target.value);
                          setPagination((prev) => ({ ...prev, current: 1 }));
                        }}
                      />
                    }
                  />
                  <Button
                    type="primary"
                    disabled={!isAddPermissionGranted}
                    icon={<PlusOutlined />}
                    onClick={() => setAddModalOpen(true)}
                  >
                    Add Music
                  </Button>
                </div>
              }
            >
              {showFilters && (
                <FilterBarFilters>
                  <Select
                    placeholder="Status"
                    value={filters.status}
                    allowClear
                    style={{ width: 150 }}
                    onChange={(value) => handleFilterChange("status", value)}
                  >
                    <Option value="true">Active</Option>
                    <Option value="false">Inactive</Option>
                  </Select>
                  <Button icon={<UndoOutlined />} onClick={handleReset}>
                    Reset
                  </Button>
                </FilterBarFilters>
              )}
              <h4 className="text-right">{ShowTotal(pagination.total || 0)}</h4>
              <Table
                loading={loading}
                columns={columns}
                dataSource={list}
                pagination={pagination}
                onChange={handleTableChange}
                rowKey="_id"
              />
            </Card>
          </Col>
        </Row>
      </div>

      <Modal
        title="Add New Music"
        open={addModalOpen}
        onCancel={() => {
          if (!modalLoading) {
            closeAndResetModal();
          }
        }}
        confirmLoading={modalLoading}
        okText="Upload"
        okButtonProps={{
          form: "add-music-form",
          htmlType: "submit",
          loading: modalLoading,
        }}
        destroyOnClose
      >
        <Form
          form={form}
          id="add-music-form"
          layout="vertical"
          onFinish={handleAddSubmit}
        >
          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: "Please enter music title" }]}
          >
            <Input placeholder="Enter music title" />
          </Form.Item>

          <Form.Item label="Artist" name="artist">
            <Input placeholder="Enter artist name" />
          </Form.Item>

          <Form.Item
            label="Audio File"
            required
            extra="Supported formats: .mp3, .wav, .m4a, .ogg (Max 100MB)"
          >
            <Upload
              beforeUpload={(file) => {
                const isValidFormat = [
                  "audio/mpeg",
                  "audio/mp3",
                  "audio/wav",
                  "audio/x-wav",
                  "audio/mp4",
                  "audio/x-m4a",
                  "audio/ogg",
                ].includes(file.type) || file.name.endsWith(".mp3") || file.name.endsWith(".wav") || file.name.endsWith(".m4a");
                
                if (!isValidFormat) {
                  ShowToast("Unsupported file format!", Severty.ERROR);
                  return Upload.LIST_IGNORE;
                }

                const isLt100M = file.size / 1024 / 1024 < 100;
                if (!isLt100M) {
                  ShowToast("Audio file must be smaller than 100MB!", Severty.ERROR);
                  return Upload.LIST_IGNORE;
                }

                setFile(file);
                if (filePreviewUrl) {
                  URL.revokeObjectURL(filePreviewUrl);
                }
                const url = URL.createObjectURL(file);
                setFilePreviewUrl(url);
                return false; // Prevent automatic upload
              }}
              onRemove={() => {
                setFile(null);
                if (filePreviewUrl) {
                  URL.revokeObjectURL(filePreviewUrl);
                  setFilePreviewUrl(null);
                }
              }}
              maxCount={1}
              fileList={file ? [file] : []}
            >
              <Button icon={<UploadOutlined />}>Select Audio File</Button>
            </Upload>
          </Form.Item>

          {filePreviewUrl && (
            <div style={{ marginTop: "15px", marginBottom: "15px", textAlign: "center" }}>
              <p style={{ marginBottom: "8px" }}><strong>Selected File Preview:</strong></p>
              <audio
                src={filePreviewUrl}
                controls
                style={{ width: "100%" }}
                onPlay={(e) => {
                  const audios = document.getElementsByTagName("audio");
                  for (let i = 0; i < audios.length; i++) {
                    if (audios[i] !== e.target) {
                      audios[i].pause();
                    }
                  }
                }}
              />
            </div>
          )}
        </Form>
      </Modal>

      <Modal
        title="Edit Music"
        open={editModalOpen}
        onCancel={() => {
          if (!modalLoading) {
            setEditModalOpen(false);
            setSelectedRecord(null);
            editForm.resetFields();
          }
        }}
        confirmLoading={modalLoading}
        okText="Update"
        okButtonProps={{
          form: "edit-music-form",
          htmlType: "submit",
          loading: modalLoading,
        }}
        destroyOnClose
      >
        <Form
          form={editForm}
          id="edit-music-form"
          layout="vertical"
          onFinish={handleEditSubmit}
        >
          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: "Please enter music title" }]}
          >
            <Input placeholder="Enter music title" />
          </Form.Item>

          <Form.Item label="Artist" name="artist">
            <Input placeholder="Enter artist name" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default MusicManager;
